import {
  Box,
  Grid,
  Typography
} from "@mui/material";
import { motion, useInView } from "framer-motion";
import React, { useRef } from "react";
import { IoSettingsOutline, IoCartOutline, IoPeopleOutline, IoCalendarOutline } from "react-icons/io5";
import { LiaStarOfLifeSolid } from "react-icons/lia";
import { HiCodeBracket } from "react-icons/hi2";

import styled from "styled-components";

const Services = React.forwardRef((props, ref) => {

  // Ref for scroll-based animation trigger
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const servicesData = [
    {
      title: "ERP Solutions",
      icon: <IoSettingsOutline />,
      description:
        "We build systems that streamline and unify essential business functions including finance, human resources, supply chain, manufacturing, and sales within a single, integrated platform for seamless operations.",
    },
    {
      title: "Agtech",
      icon: <HiCodeBracket />,
      description:
        "A platform for digital training material delivery and for tracking farm operations to strengthen the agricultural extension system. It is accessible on android smartphones aimed to provide support during the production season.",
    },
    {
      title: "Health",
      icon: <LiaStarOfLifeSolid />,
      description:
        "Developed an innovative e-health solution that streamlines patient care, enhances medical data management, and improves healthcare delivery through secure, integrated platforms.",
    },
    {
      title: "Procurement",
      icon: <IoCartOutline />,
      description:
        "Built advanced procurement solutions that automate and optimize purchasing processes, supplier management, and cost tracking. These tools enhance efficiency, ensure compliance, and provide real-time insights for smarter decision-making.",
    },
    {
      title: "Tourism",
      icon: <IoPeopleOutline />,
      description:
        "With AJT, we have developed a robust tourism booking platform that simplifies reservations, itinerary planning, and customer management for travel businesses across Africa with seamless payment integration and multiple language support.",
    },
    {
      title: "MICE",
      icon: <IoCalendarOutline />,
      description:
        "Our flagship in-house product Owlevents is a comprehensive, multi-lingual, multi-currency, and dynamic platform that seamlessly provides solutions to the event industry.",
    },
  ];

  const SpacerBox = styled(Box)({
    height: "30px",
  });

  // Animation variants for the section title and description
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2,
      },
    },
  };

  // Animation variants for individual service cards
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
    hover: {
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      borderRadius: 10,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  return (
    <Box ref={ref} textAlign="center" px={2} >
      <motion.div
        ref={sectionRef}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={sectionVariants}
      >
        <motion.div variants={sectionVariants}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '24px', sm: '30px', md: '40px' },
              mb: 3,
              color: '#000000',
              textAlign: 'center',
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '120%',
                height: '3px',
                backgroundColor: '#17273F',
              }
            }}
          >
            Services
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '14px', sm: '16px', md: '20px' },
              color: '#4B5563',
              textAlign: 'center',
              mb: 6,
              mx: 'auto',
            }}
          >
            Empowering businesses with innovative technology solution that drive growth and efficiency
          </Typography>
        </motion.div>
      
      </motion.div>

      <Box display="flex" justifyContent="center" sx={{ pb: 3 }}>
        <Grid container spacing={{ xs: 3, sm: 4, md: 4 }} justifyContent="center" sx={{ maxWidth: '1400px', margin: '5 auto', px: { xs: 2, sm: 3, md: 3 } }}>
          {servicesData.map((service, index) => (
            <Grid item xs={6} sm={6} md={4} key={index}>
              <motion.div
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={cardVariants}
                whileHover="hover"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  p={{ xs: 1.5, sm: 2.5, md: 3 }}
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    height: { xs: "200px", sm: "345px", md: "358px" },
                    width: "100%",
                    mx: "auto",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transform: "translateY(-0.5px)",
                    }
                  }}
                >
                  <Box
                    mb={2}
                    sx={{
                      fontSize: { xs: "22px", sm: "32px", md: "32px" },
                      color: "#17273F",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "15px",
                      width: { xs: "45px", sm: "64px", md: "65px" },
                      height: { xs: "45px", sm: "64px", md: "65px" },
                      borderRadius: 2,
                      backgroundColor: "#f0f7ff",
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "12px", sm: "25px", md: "25px" },
                      fontWeight: 600,
                      mb: 1.5,
                      color: "#000000",
                      lineHeight: 1.3
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "7px", sm: "18px" },
                      color: "#4B5563",
                      lineHeight: { xs: "11px", sm: "26px", md: "26px" },
                      textAlign: "left",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: { xs: 8, sm: 6, md: 7 },
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {service.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      <SpacerBox />

      <motion.div
        initial={{ width: 0 }}
        animate={isInView ? { width: "100%" } : { width: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
      </motion.div>

      <SpacerBox />
    </Box>
  );
});

export default Services;

const MainTitle = styled.h1`
  color: bls;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;